import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { toast } from '@/lib/toasts/api'
import {
  useEmailImiDeclaration,
  invalidateImiDeclarationsSnapshot,
  useImiDeclarations,
  usePrintImiDeclaration,
  useWithdrawImiDeclaration,
  useSubmitImiDeclaration,
  buildImiPdfFilename,
  fetchImiDeclarationPdfBytes,
} from '../services/useImiDeclarationsService'
import type { ImiDeclarationItem } from '../services/useImiDeclarationsService'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { TaskStatusWrapper } from '@/modules/shared/components/TaskStatusWrapper'
import { getApiErrorMessage } from '@/modules/shared/utils/apiError'
import { RouteComponentProps } from '@/app/navigation/withRouter'
import type { BasicDriver } from '@/lib/api/company/drivers/useQueryBasicDrivers'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import { FieldsetSelect } from '@/modules/settings/components/FieldsetSelect'
import { useImiSidePanel } from '../context/ImiSidePanelContext'
import { ImiProgressBanner } from '../components/ImiProgressBanner'
import {
  buildImiPackageFilename,
  createDriverPackageZip,
  downloadBlob,
  getDeclarationDateRange,
  groupDeclarationsByDriver,
  resolveDriverNameParts,
  toIsoDateString,
} from '../utils/imiPackageUtils'
import { useExport } from '../stores/exportStore'
import {
  faArrowLeftFromBracketSolid,
  faBallotCheckSolid,
  faEnvelopeSolid,
  FaIcon,
  faPlusSolid,
  faPrintSolid,
  faRefreshSolid,
} from '@/lib/fa'
import Tooltip from '@/modules/drivers/shared/ToolTip'
import DividerVertical from '@/modules/drivers/shared/DividerVertical'
import ImiDeclarationsTable from '../components/imiTable/ImiDeclarationsTable'
import { LoaderIcon } from '@/app/features/icons/Loader'

type MatchParams = {
  companyId: string
}

type DeclarationAction = 'print' | 'email' | 'withdraw' | 'submit'

type ImiStatus = 'ACTIVE' | 'WITHDRAWN' | 'EXPIRED'

const STATUS_LABEL_KEYS: Record<ImiStatus, string> = {
  ACTIVE: 'exportModule.status-active',
  WITHDRAWN: 'exportModule.status-withdrawn',
  EXPIRED: 'exportModule.status-expired',
}

const STATUS_LABEL_FALLBACK: Record<ImiStatus, string> = {
  ACTIVE: 'Aktywna',
  WITHDRAWN: 'Wycofana',
  EXPIRED: 'Wygasła',
}

const getStatusLabel = (status: ImiStatus, t: (key: string) => string) =>
  t(STATUS_LABEL_KEYS[status]) || STATUS_LABEL_FALLBACK[status]


const deriveImiStatus = (item: {
  declarationStatus?: string
  declarationEndDate?: string
}): ImiStatus => {
  const raw = (item.declarationStatus || '').toUpperCase()
  if (raw === 'WITHDRAWN' || raw === 'CANCELLED') return 'WITHDRAWN'
  if (raw === 'EXPIRED') return 'EXPIRED'

  if (item.declarationEndDate) {
    const end = new Date(`${item.declarationEndDate}T23:59:59`)
    if (!Number.isNaN(end.getTime()) && end < new Date()) return 'EXPIRED'
  }
  return 'ACTIVE'
}


const getDriverLabel = (d: BasicDriver) => d.fullName?.trim() || ''

const sleep = (ms: number) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })

const IMIDeclarationsPage: React.FC<RouteComponentProps<MatchParams>> = ({
  match,
}) => {
  const { companyId } = match.params
  const navigate = useNavigate()
  const { t, activeLanguage } = useLocalize()
  const imiLanguage = activeLanguage?.code || 'pl'

  const [status, setStatus] = React.useState('ACTIVE')
  const { basicDrivers } = useDrivers()
  const { selectedDriverIds: driverFilterIds } = useImiSidePanel()

  const statusOptions = React.useMemo(
    () => [
      { value: '', label: t('common.all') },
      { value: 'ACTIVE', label: getStatusLabel('ACTIVE', t) },
      { value: 'WITHDRAWN', label: getStatusLabel('WITHDRAWN', t) },
      { value: 'EXPIRED', label: getStatusLabel('EXPIRED', t) },
    ],
    [t],
  )

  const prevCompanyRef = React.useRef(companyId)
  React.useEffect(() => {
    if (prevCompanyRef.current === companyId) return
    prevCompanyRef.current = companyId
    setStatus('ACTIVE')
    setSelectedDeclarationIds([])
  }, [companyId])
  const { dateRange } = useExport()

  const filterDateBounds = React.useMemo(() => {
    const start = dateRange?.startDate
    const end = dateRange?.endDate

    if (
      start?.year == null ||
      start?.month == null ||
      start?.day == null ||
      end?.year == null ||
      end?.month == null ||
      end?.day == null
    ) {
      return null
    }

    return {
      start: toIsoDateString(start.year, start.month, start.day),
      end: toIsoDateString(end.year, end.month, end.day),
    }
  }, [
    dateRange?.startDate?.year,
    dateRange?.startDate?.month,
    dateRange?.startDate?.day,
    dateRange?.endDate?.year,
    dateRange?.endDate?.month,
    dateRange?.endDate?.day,
  ])

  const selectedDriverNames = React.useMemo(
    () =>
      basicDrivers
        .filter((d) => driverFilterIds.includes(d.id.toString()))
        .map((d) => getDriverLabel(d))
        .filter(Boolean),
    [basicDrivers, driverFilterIds],
  )

  // Filtry liczy backend na migawce: nieprzefiltrowany zbior ma ~28 tys. pozycji,
  // wiec przepuszczanie go w calosci do przegladarki byloby ~7,6 MB.
  const declarationsQuery = useImiDeclarations(companyId, {
    status,
    dateFrom: filterDateBounds?.start,
    dateTo: filterDateBounds?.end,
    driverNames: selectedDriverNames,
  })

  const printMutation = usePrintImiDeclaration(companyId)
  const emailMutation = useEmailImiDeclaration(companyId, imiLanguage)
  const withdrawMutation = useWithdrawImiDeclaration(companyId)
  const submitMutation = useSubmitImiDeclaration(companyId)
  const [selectedDeclarationIds, setSelectedDeclarationIds] = React.useState<
    string[]
  >([])

  const [activeDeclarationId, setActiveDeclarationId] = React.useState<
    string | null
  >(null)
  const [activeBulkAction, setActiveBulkAction] =
    React.useState<DeclarationAction | null>(null)
  const [bulkProgress, setBulkProgress] = React.useState<{
    current: number
    total: number
  } | null>(null)

  const isCurrentDeclaration = React.useCallback(
    (declarationStatus?: string, declarationEndDate?: string) => {
      const terminalStatuses = new Set(['WITHDRAWN', 'EXPIRED', 'CANCELLED'])
      if (!declarationStatus || terminalStatuses.has(declarationStatus)) {
        return false
      }

      if (!declarationEndDate) {
        return true
      }

      const endDate = new Date(`${declarationEndDate}T23:59:59`)
      if (Number.isNaN(endDate.getTime())) {
        return true
      }

      return endDate >= new Date()
    },
    [],
  )

  // `declarationsQuery` to nowy obiekt przy kazdym renderze; zalezenie od niego
  // w useCallback psulo React.memo na wierszach. `refetch` jest stabilne.
  const refetchDeclarations = declarationsQuery.refetch

  // Backend oddaje caly odfiltrowany zbior, wiec lista = komplet pasujacych pozycji.
  const visibleDeclarations = React.useMemo(
    () => declarationsQuery.data?.items ?? [],
    [declarationsQuery.data?.items],
  )

  const totalCount = declarationsQuery.data?.totalCount ?? 0

  const filterSignature = `${companyId}|${status}|${filterDateBounds?.start ?? ''}|${
    filterDateBounds?.end ?? ''
  }|${selectedDriverNames.join(',')}`

  // Zaznaczenie odnosi się do konkretnego filtra - po jego zmianie traci sens.
  React.useEffect(() => {
    setSelectedDeclarationIds([])
  }, [filterSignature])

  const isWithdrawable = React.useCallback(
    (item: { declarationStatus?: string; declarationEndDate?: string }) =>
      isCurrentDeclaration(item.declarationStatus, item.declarationEndDate) &&
      item.declarationStatus !== 'DRAFT',
    [isCurrentDeclaration],
  )

  const executeDeclarationAction = React.useCallback(
    async (action: DeclarationAction, declaration: ImiDeclarationItem) => {
      const declarationId = declaration.declarationId
      switch (action) {
        case 'print':
          await printMutation.mutateAsync({
            declarationId,
            item: declaration,
            language: imiLanguage,
          })
          return
        case 'email':
          await emailMutation.mutateAsync({ declarationId })
          return
        case 'withdraw':
          await withdrawMutation.mutateAsync({ declarationId })
          return
        case 'submit':
          await submitMutation.mutateAsync({ declarationId })
          return
        default:
          return
      }
    },
    [
      emailMutation,
      imiLanguage,
      printMutation,
      submitMutation,
      withdrawMutation,
    ],
  )

  const getActionSuccessMessage = React.useCallback(
    (action: DeclarationAction) => {
      switch (action) {
        case 'print':
          return t('exportModule.imi-print-success')
        case 'email':
          return t('exportModule.imi-email-success')
        case 'withdraw':
          return t('exportModule.imi-withdraw-success')
        case 'submit':
          return (
            t('exportModule.imi-submit-success') || 'Zgłoszenie zostało wysłane'
          )
        default:
          return t('common.saved')
      }
    },
    [t],
  )

  const getActionErrorMessage = React.useCallback(
    (action: DeclarationAction) => {
      switch (action) {
        case 'print':
          return t('exportModule.imi-print-error')
        case 'email':
          return t('exportModule.imi-email-error')
        case 'withdraw':
          return t('exportModule.imi-withdraw-error')
        case 'submit':
          return (
            t('exportModule.imi-submit-error') ||
            'Błąd podczas wysyłania zgłoszenia'
          )
        default:
          return t('common.error')
      }
    },
    [t],
  )

  const getBulkActionLabel = React.useCallback(
    (action: DeclarationAction) => {
      switch (action) {
        case 'print':
          return t('common.print') || 'Drukuj'
        case 'email':
          return t('exportModule.send-email') || 'Wyślij mailem'
        case 'withdraw':
          return t('exportModule.withdraw') || 'Wycofaj'
        case 'submit':
          return t('exportModule.submit-declaration') || 'Zatwierdź'
        default:
          return ''
      }
    },
    [t],
  )

  const handleSingleAction = React.useCallback(
    async (action: DeclarationAction, declaration: ImiDeclarationItem) => {
      setActiveDeclarationId(declaration.declarationId)
      try {
        await executeDeclarationAction(action, declaration)
        toast.success({
          text: getActionSuccessMessage(action),
        })
        if (action === 'submit' || action === 'withdraw') {
          try {
            await invalidateImiDeclarationsSnapshot(companyId)
          } catch {
            // Jw. - akcja się powiodła, nie zgłaszaj tego jako jej błąd.
          }
          refetchDeclarations()
        }
      } catch (error: any) {
        toast.error({
          text: getActionErrorMessage(action),
          content: getApiErrorMessage(error),
        })
      } finally {
        setActiveDeclarationId(null)
      }
    },
    [
      companyId,
      refetchDeclarations,
      executeDeclarationAction,
      getActionErrorMessage,
      getActionSuccessMessage,
    ],
  )

  const selectedDeclarations = React.useMemo(() => {
    // Set, bo zaznaczenie "wszystkich" to tysiące pozycji po obu stronach.
    const selected = new Set(selectedDeclarationIds)
    return visibleDeclarations.filter((item) => selected.has(item.declarationId))
  }, [visibleDeclarations, selectedDeclarationIds])

  const getBulkActionCandidates = React.useCallback(
    (action: DeclarationAction) => {
      switch (action) {
        case 'submit':
          return selectedDeclarations.filter(
            (item) => item.declarationStatus === 'DRAFT',
          )
        case 'withdraw':
          return selectedDeclarations.filter(isWithdrawable)
        default:
          return selectedDeclarations
      }
    },
    [isWithdrawable, selectedDeclarations],
  )

  const handleBulkPrint = React.useCallback(
    async (
      candidates: ImiDeclarationItem[],
      onProgress?: (processed: number) => void,
    ) => {
      const groups = groupDeclarationsByDriver(candidates, basicDrivers)
      let processed = 0

      for (const group of groups) {
        const pdfs: { filename: string; bytes: Uint8Array }[] = []

        for (const declaration of group.declarations) {
          const bytes = await fetchImiDeclarationPdfBytes(
            companyId,
            declaration.declarationId,
            imiLanguage,
          )
          pdfs.push({
            filename: buildImiPdfFilename(declaration),
            bytes,
          })
          processed += 1
          onProgress?.(processed)
          await sleep(250)
        }

        const zipBytes = await createDriverPackageZip(pdfs)
        const { dateFrom, dateTo } = getDeclarationDateRange(group.declarations)
        const { surname, name } = resolveDriverNameParts(group)
        const packageFilename = buildImiPackageFilename(
          surname,
          name,
          dateFrom,
          dateTo,
        )

        downloadBlob(
          new Blob([zipBytes], { type: 'application/zip' }),
          packageFilename,
        )
        await sleep(250)
      }
    },
    [basicDrivers, companyId, imiLanguage],
  )

  const handleBulkAction = React.useCallback(
    async (action: DeclarationAction) => {
      if (selectedDeclarationIds.length === 0) {
        toast.warning({
          text: 'Najpierw zaznacz deklaracje do wykonania operacji masowej.',
        })
        return
      }

      const candidates = getBulkActionCandidates(action)
      if (candidates.length === 0) {
        toast.warning({
          text: `Brak zaznaczonych deklaracji dostępnych dla akcji: ${getBulkActionLabel(action).toLowerCase()}.`,
        })
        return
      }

      setActiveBulkAction(action)
      setBulkProgress({ current: 0, total: candidates.length })
      let successCount = 0
      let failedCount = 0
      let lastError = ''

      try {
        if (action === 'print') {
          try {
            await handleBulkPrint(candidates, (processed) =>
              setBulkProgress({ current: processed, total: candidates.length }),
            )
            successCount = candidates.length
          } catch (error: any) {
            failedCount = candidates.length
            lastError = getApiErrorMessage(error) || 'Błąd'
          }
        } else {
          for (let i = 0; i < candidates.length; i++) {
            const declaration = candidates[i]
            setActiveDeclarationId(declaration.declarationId)
            try {
              await executeDeclarationAction(action, declaration)
              successCount++
            } catch (error: any) {
              failedCount++
              lastError = getApiErrorMessage(error) || 'Błąd'
            }
            setBulkProgress({ current: i + 1, total: candidates.length })
          }
        }

        const skippedCount = selectedDeclarationIds.length - candidates.length
        const actionLabel = getBulkActionLabel(action)

        if (failedCount === 0) {
          toast.success({
            text:
              skippedCount > 0
                ? `${actionLabel}: wykonano dla ${successCount} deklaracji, pominięto ${skippedCount}.`
                : `${actionLabel}: wykonano dla ${successCount} deklaracji.`,
          })
        } else if (successCount > 0) {
          toast.warning({
            text: `${actionLabel}: powodzenie ${successCount}, błędy ${failedCount}, pominięto ${skippedCount}.`,
            content: lastError,
          })
        } else {
          toast.error({
            text: `${actionLabel}: operacja nie powiodła się dla wszystkich zaznaczonych deklaracji.`,
            content: lastError,
          })
        }

        if (action === 'submit' || action === 'withdraw') {
          // Zaznaczone id po akcji są nieaktualne - lista zmieniła zawartość.
          setSelectedDeclarationIds([])
          // Przebudowanie migawki to 30 zapytań do IMI i potrafi przekroczyć timeout.
          // Akcja już się wykonała, więc odświeżenie listy musi pójść tak czy inaczej.
          try {
            await invalidateImiDeclarationsSnapshot(companyId)
          } catch {
            // Migawka dożyje swojego TTL - lista i tak się przeładuje.
          }
          refetchDeclarations()
        }
      } finally {
        setActiveDeclarationId(null)
        setActiveBulkAction(null)
        setBulkProgress(null)
      }
    },
    [
      refetchDeclarations,
      executeDeclarationAction,
      getBulkActionCandidates,
      getBulkActionLabel,
      handleBulkPrint,
      selectedDeclarationIds.length,
      companyId,
    ],
  )

  const isSubmittingInProgress =
    activeBulkAction === 'submit' ||
    (activeDeclarationId !== null && submitMutation.isPending)
  const isPrintingInProgress = activeBulkAction === 'print'

  const selectedCount = selectedDeclarationIds.length
  const isAnyDeclarationMutationPending =
    printMutation.isPending ||
    emailMutation.isPending ||
    withdrawMutation.isPending ||
    submitMutation.isPending
  const isSelectionLocked =
    activeBulkAction !== null || isAnyDeclarationMutationPending
  const selectedIdSet = React.useMemo(
    () => new Set(selectedDeclarationIds),
    [selectedDeclarationIds],
  )
  const allVisibleSelected =
    visibleDeclarations.length > 0 &&
    visibleDeclarations.every((item) => selectedIdSet.has(item.declarationId))
  const hasPartiallySelectedVisible =
    !allVisibleSelected &&
    visibleDeclarations.some((item) => selectedIdSet.has(item.declarationId))

  const isBusy = React.useCallback(
    (declarationId: string) =>
      activeBulkAction !== null ||
      (activeDeclarationId === declarationId && isAnyDeclarationMutationPending),
    [activeBulkAction, activeDeclarationId, isAnyDeclarationMutationPending],
  )

  const handleToggleSelect = React.useCallback((declarationId: string) => {
    setSelectedDeclarationIds((prev) =>
      prev.includes(declarationId)
        ? prev.filter((id) => id !== declarationId)
        : [...prev, declarationId],
    )
  }, [])

  const handleToggleSelectAll = React.useCallback(
    (checked: boolean) => {
      setSelectedDeclarationIds(
        checked ? visibleDeclarations.map((item) => item.declarationId) : [],
      )
    },
    [visibleDeclarations],
  )

  const getRowStatusLabel = React.useCallback(
    (item: ImiDeclarationItem) => getStatusLabel(deriveImiStatus(item), t),
    [t],
  )


  const renderBulkIcon = React.useCallback(
    (
      action: DeclarationAction,
      icon: typeof faPrintSolid,
      className: string,
    ) =>
      activeBulkAction === action ? (
        <LoaderIcon className="tw-h-4 tw-w-4 tw-animate-spin" />
      ) : (
        <FaIcon className={className} icon={icon} />
      ),
    [activeBulkAction],
  )

  return (
    <TaskStatusWrapper>
      {(isSubmittingInProgress || isPrintingInProgress) && (
        <div className="tw-pointer-events-none tw-fixed tw-right-4 tw-top-4 tw-z-[300]">
          <ImiProgressBanner
            messageKey={
              isPrintingInProgress
                ? 'exportModule.printing-in-progress'
                : 'exportModule.submitting-in-progress'
            }
            fallback={
              isPrintingInProgress ? 'Trwa drukowanie…' : 'Trwa zgłaszanie…'
            }
            current={bulkProgress?.current}
            total={bulkProgress?.total}
          />
        </div>
      )}
      <section className="tw-flex tw-h-full tw-min-h-0 tw-w-full tw-flex-col tw-overflow-hidden">
        <section className="tw-flex tw-w-full tw-min-h-0 tw-flex-1 tw-flex-col">
            <div className="tw-z-[100] tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-base-300 tw-px-4 tw-py-2">
              <div className="tw-flex tw-items-center tw-gap-2">
                <div
                  onClick={() => navigate('../imi-add')}
                  className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 hover:tw-opacity-80"
                >
                  <p className="tw-font-medium tw-text-primary">
                    Dodaj deklarację
                  </p>
                  <FaIcon className="tw-text-primary" icon={faPlusSolid} />
                </div>
                <DividerVertical />

                <div className="tw-flex tw-items-center tw-gap-2">
                  <div className="tw-w-48">
                    <FieldsetSelect
                      name="imi-status"
                      value={status}
                      options={statusOptions}
                      handleChange={(value) => setStatus(value)}
                      searchEnabled={false}
                      background
                    />
                  </div>
                </div>
              </div>

              <div className="tw-flex tw-items-center tw-gap-2">
                <Tooltip
                  onClick={() => handleBulkAction('submit')}
                  text="Zatwierdź deklarację"
                  disabled={selectedCount === 0 || isSelectionLocked}
                >
                  {renderBulkIcon(
                    'submit',
                    faBallotCheckSolid,
                    'tw-text-green-500',
                  )}
                </Tooltip>

                <DividerVertical />

                <Tooltip
                  onClick={() => handleBulkAction('email')}
                  text="Wyślij deklarację mailem"
                  disabled={selectedCount === 0 || isSelectionLocked}
                >
                  {renderBulkIcon('email', faEnvelopeSolid, 'tw-text-primary')}
                </Tooltip>
                <Tooltip
                  onClick={() => handleBulkAction('withdraw')}
                  text="Wycofaj deklarację"
                  disabled={selectedCount === 0 || isSelectionLocked}
                >
                  {renderBulkIcon(
                    'withdraw',
                    faArrowLeftFromBracketSolid,
                    'tw-text-primary',
                  )}
                </Tooltip>
                <Tooltip
                  text="Drukuj deklarację"
                  onClick={() => handleBulkAction('print')}
                  disabled={selectedCount === 0 || isSelectionLocked}
                >
                  {renderBulkIcon('print', faPrintSolid, 'tw-text-primary')}
                </Tooltip>
                <DividerVertical />
                <Tooltip
                  text="Odśwież deklarację"
                  disabled={declarationsQuery.isFetching}
                >
                  <FaIcon
                    className={`tw-text-primary ${
                      declarationsQuery.isFetching ? 'tw-animate-spin' : ''
                    }`}
                    icon={faRefreshSolid}
                    onClick={async () => {
                      try {
                        await invalidateImiDeclarationsSnapshot(companyId)
                      } catch {
                        // Migawka dozyje swojego TTL - lista i tak sie przeladuje.
                      }
                      refetchDeclarations()
                    }}
                  />
                </Tooltip>
              </div>
            </div>
            {selectedCount > 0 && (
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-border-b tw-border-base-300 tw-bg-base-200/60 tw-px-4 tw-py-2 tw-text-sm">
                <span>
                  Zaznaczono <b>{selectedCount}</b> z <b>{totalCount}</b>{' '}
                  deklaracji.
                </span>
                <button
                  type="button"
                  disabled={isSelectionLocked}
                  onClick={() => setSelectedDeclarationIds([])}
                  className="tw-text-base-content/60 hover:tw-underline disabled:tw-opacity-50"
                >
                  Wyczyść zaznaczenie
                </button>
              </div>
            )}
            {/* Declarations table */}
            <div className="tw-flex tw-h-full tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-bg-base-100">
              <ImiDeclarationsTable
                declarations={visibleDeclarations}
                isLoading={declarationsQuery.isLoading}
                isFetching={
                  declarationsQuery.isFetching && !declarationsQuery.isLoading
                }
                selectedDeclarationIds={selectedDeclarationIds}
                isSelectionLocked={isSelectionLocked}
                allVisibleSelected={allVisibleSelected}
                hasPartiallySelectedVisible={hasPartiallySelectedVisible}
                isBusy={isBusy}
                isWithdrawable={isWithdrawable}
                getStatusLabel={getRowStatusLabel}
                onToggleSelect={handleToggleSelect}
                onToggleSelectAll={handleToggleSelectAll}
                onAction={handleSingleAction}
              />
            </div>
        </section>
      </section>
    </TaskStatusWrapper>
  )
}

export { IMIDeclarationsPage }
export default IMIDeclarationsPage
